import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Typography,
  Table,
  Space,
  Button,
  Select,
  Input,
  InputNumber,
  message,
  Tag,
  Tooltip,
  Collapse,
  Alert,
  Form,
  Modal,
  Switch,
  Popconfirm,
  Spin,
  Empty,
  Divider,
  Tabs,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  LoadingOutlined,
  ExclamationCircleOutlined,
  CodeOutlined,
} from "@ant-design/icons";
import { useGlobal } from "../../contexts/GlobalContext";
import request from "../../utils/request";
import { MCPServerInfo, ToolInfo } from "../../types/MCPTypes";

const { Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

interface MCPServerConfig {
  type: string;
  url: string;
}

interface MCPServersConfig {
  [key: string]: MCPServerConfig;
}

const REQUEST_TIMEOUT = 30000;

// 禁止添加的域名列表
const BLOCKED_DOMAINS = [
  "mcp.hm.tianli0.top",
  "localhost",
  "127.0.0.1",
  "ai.zhheo.com",
];

const normalizeServerTools = (tools: any[] = []) =>
  tools.map((tool) => ({
    ...tool,
    description: tool.description || "",
    disabled:
      typeof tool.disabled === "boolean"
        ? tool.disabled
        : !tool.enabled,
    input_schema: tool.input_schema || tool.inputSchema,
    enabled: Boolean(tool.enabled),
    risk: tool.risk || "read",
    scopes: Array.isArray(tool.scopes) ? tool.scopes : [],
    maxCallsPerExecution: tool.maxCallsPerExecution || 1,
  }));

const MCPClientTab: React.FC = () => {
  const { projects, API_BASE_URL, token, refreshProjects } = useGlobal();
  const [servers, setServers] = useState<MCPServerInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalTitle, setModalTitle] = useState("添加MCP服务器");
  const [currentServer, setCurrentServer] = useState<MCPServerInfo | null>(
    null
  );
  const [selectedProject, setSelectedProject] = useState<number | null>(null);
  const [testLoading, setTestLoading] = useState(false);
  const [testStatus, setTestStatus] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [serverTools, setServerTools] = useState<ToolInfo[]>([]);
  const [toolsModalVisible, setToolsModalVisible] = useState(false);
  const [selectedServer, setSelectedServer] = useState<MCPServerInfo | null>(
    null
  );
  const [toolsLoading, setToolsLoading] = useState(false);
  const [authorizationSaving, setAuthorizationSaving] = useState(false);
  const [scopedServerEnabled, setScopedServerEnabled] = useState(false);
  const [form] = Form.useForm();
  const [jsonForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState<string>("form");
  const [jsonParseError, setJsonParseError] = useState<string | null>(null);
  // 新增项目MCP启用状态
  const [projectMCPEnabled, setProjectMCPEnabled] = useState<boolean>(false);

  // 过滤只获取 PostChat 项目
  const postChatProjects = projects.filter((project) => project.is_postchat);

  // 检查URL是否包含禁止的域名
  const isBlockedDomain = (url: string): boolean => {
    try {
      const urlObj = new URL(url);
      return BLOCKED_DOMAINS.some((domain) => urlObj.hostname.includes(domain));
    } catch (e) {
      return false;
    }
  };

  // 组件加载时获取服务器列表
  const fetchServers = useCallback(
    async (projectId?: number) => {
      setLoading(true);
      try {
        const response = await request.get<{
          code: number;
          msg: string;
          data: MCPServerInfo[];
        }>(`${API_BASE_URL}/api/v3/mcp/servers`, {
          params: {
            project_id: projectId || selectedProject || postChatProjects[0]?.id,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
          timeout: REQUEST_TIMEOUT, // 设置超时时间
        });

        if (response.data.code === 200) {
          setServers(response.data.data || []);
        } else {
          message.error(response.data.msg || "获取MCP服务器列表失败");
        }
      } catch (error: any) {
        console.error("获取MCP服务器列表失败:", error);
        if (error.code === "ECONNABORTED") {
          message.error("请求超时，请检查网络连接");
        } else {
          message.error(error.response?.data?.msg || "获取MCP服务器列表失败");
        }
      } finally {
        setLoading(false);
      }
    },
    [API_BASE_URL, postChatProjects, selectedProject, token]
  );

  // 测试MCP服务器连接
  const testMCPServer = async () => {
    if (!currentServer?.id) {
      setTestStatus({
        success: false,
        message: "请先保存MCP服务器，再执行连接测试",
      });
      return false;
    }

    setTestLoading(true);
    setTestStatus(null);
    try {
      const response = await request.post<{
        code: number;
        msg: string;
        data: {
          status: string;
          message: string;
          tools_count: number;
        };
      }>(`${API_BASE_URL}/api/v3/mcp/servers/${currentServer.id}/test`, null, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        timeout: REQUEST_TIMEOUT,
      });

      if (response.data.code === 200 && response.data.data?.status === "ok") {
        await fetchServerTools(currentServer.id);
        setTestStatus({
          success: true,
          message:
            response.data.data?.message ||
            `连接成功，发现 ${response.data.data?.tools_count || 0} 个工具`,
        });
        return true;
      } else {
        setTestStatus({
          success: false,
          message:
            response.data.data?.message || response.data.msg || "连接测试失败",
        });
        return false;
      }
    } catch (error: any) {
      console.error("测试MCP服务器连接失败:", error);
      const errorMessage =
        error.code === "ECONNABORTED"
          ? "连接测试超时，请确认服务器地址是否正确或者网络是否稳定"
          : error.response?.data?.msg ||
            "连接测试失败，请检查服务器地址和API密钥";

      setTestStatus({
        success: false,
        message: errorMessage,
      });
      return false;
    } finally {
      setTestLoading(false);
    }
  };

  // 获取特定服务器的工具列表
  const fetchServerTools = async (serverId: number) => {
    setToolsLoading(true);
    try {
      const response = await request.get<{
        code: number;
        msg: string;
        data: any[];
      }>(`${API_BASE_URL}/api/v3/mcp/servers/${serverId}/tools`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        timeout: REQUEST_TIMEOUT,
      });

      if (response.data.code === 200) {
        setServerTools(normalizeServerTools(response.data.data || []));
      } else {
        message.error(response.data.msg || "获取工具列表失败");
      }
    } catch (error: any) {
      console.error("获取MCP服务器工具列表失败:", error);
      if (error.code === "ECONNABORTED") {
        message.error("获取工具列表超时，请稍后重试");
      } else {
        message.error(error.response?.data?.msg || "获取工具列表失败");
      }
    } finally {
      setToolsLoading(false);
    }
  };

  // 切换工具禁用状态
  const toggleToolStatus = async (toolName: string, disabled: boolean) => {
    if (!selectedServer) return;
	if (selectedServer.authorization_mode === "scoped") {
		setServerTools((tools) =>
			tools.map((tool) => tool.name === toolName ? { ...tool, enabled: !disabled, disabled } : tool)
		);
		return;
	}

    try {
      const response = await request.patch<{
        code: number;
        msg: string;
      }>(
        `${API_BASE_URL}/api/v3/mcp/servers/${selectedServer.id}/tools/toggle`,
        {
          tool_name: toolName,
          disabled: disabled,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          timeout: REQUEST_TIMEOUT,
        }
      );

      if (response.data.code === 200) {
        message.success(disabled ? "工具已禁用" : "工具已启用");
        // 更新工具状态
        setServerTools((tools) =>
          tools.map((tool) =>
            tool.name === toolName ? { ...tool, disabled } : tool
          )
        );
      } else {
        message.error(response.data.msg || "操作失败");
      }
    } catch (error: any) {
      console.error("切换工具状态失败:", error);
      message.error(error.response?.data?.msg || "操作失败");
    }
  };

  const updateScopedTool = (toolName: string, patch: Partial<ToolInfo>) => {
    setServerTools((tools) =>
      tools.map((tool) => tool.name === toolName ? { ...tool, ...patch } : tool)
    );
  };

  const saveScopedAuthorization = async () => {
    if (!selectedServer || selectedServer.authorization_mode !== "scoped") return;
    const enabledTools = serverTools.filter((tool) => tool.enabled);
    if (scopedServerEnabled && enabledTools.length === 0) {
      message.error("启用服务器前至少需要授权一个工具");
      return;
    }
    for (const tool of enabledTools) {
      if (!tool.scopes?.length) {
        message.error(`工具 ${tool.name} 尚未填写允许 Scope`);
        return;
      }
    }
    const policies = Object.fromEntries(enabledTools.map((tool) => [tool.name, {
      enabled: true,
      risk: tool.risk || "read",
      scopes: tool.scopes,
      maxCallsPerExecution: tool.maxCallsPerExecution || 1,
    }]));
    setAuthorizationSaving(true);
    try {
      const response = await request.put<{ code: number; msg: string; data: MCPServerInfo }>(
        `${API_BASE_URL}/api/v3/mcp/servers/${selectedServer.id}/authorization`,
        { enabled: scopedServerEnabled, policies },
        { headers: { Authorization: `Bearer ${token}` }, timeout: REQUEST_TIMEOUT }
      );
      if (response.data.code !== 200) throw new Error(response.data.msg || "保存授权失败");
      setSelectedServer(response.data.data);
      message.success("后端工具授权已保存");
      await fetchServers();
      await fetchServerTools(selectedServer.id);
    } catch (error: any) {
      message.error(error.response?.data?.msg || error.message || "保存授权失败");
    } finally {
      setAuthorizationSaving(false);
    }
  };

  // 添加或更新MCP服务器
  const handleAddOrUpdateServer = async (values: any) => {
    if (isBlockedDomain(values.endpoint)) {
      message.error("不允许添加该域名的服务器，请使用其他MCP服务器");
      return;
    }

    if (currentServer) {
      // 更新服务器
      try {
        const response = await request.put<{
          code: number;
          msg: string;
          data: MCPServerInfo;
        }>(
          `${API_BASE_URL}/api/v3/mcp/servers/${currentServer.id}`,
          {
            name: values.name,
            endpoint: values.endpoint,
            enabled: values.enabled,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            timeout: REQUEST_TIMEOUT,
          }
        );

        if (response.data.code === 200) {
          message.success("MCP服务器更新成功");
          setModalVisible(false);
          fetchServers();
        } else {
          message.error(response.data.msg || "更新失败");
        }
      } catch (error: any) {
        console.error("更新MCP服务器失败:", error);
        message.error(error.response?.data?.msg || "更新失败");
      }
    } else {
      // 添加服务器
      try {
        const response = await request.post<{
          code: number;
          msg: string;
          data: MCPServerInfo;
        }>(
          `${API_BASE_URL}/api/v3/mcp/servers`,
          {
            name: values.name,
            endpoint: values.endpoint,
            project_id: values.project_id,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            timeout: REQUEST_TIMEOUT,
          }
        );

        if (response.data.code === 200) {
          message.success("MCP服务器添加成功");
          setModalVisible(false);
          fetchServers();
        } else {
          message.error(response.data.msg || "添加失败");
        }
      } catch (error: any) {
        console.error("添加MCP服务器失败:", error);
        message.error(error.response?.data?.msg || "添加失败");
      }
    }
  };

  // 通过JSON配置添加MCP服务器
  const handleAddServersByJson = async (values: any) => {
    try {
      setJsonParseError(null);
      const jsonConfig = JSON.parse(values.jsonConfig);
      const mcpServers = jsonConfig.mcpServers as MCPServersConfig;

      if (!mcpServers) {
        setJsonParseError("无效的配置格式，缺少 mcpServers 字段");
        return;
      }

      const projectId = values.project_id;
      let addedCount = 0;
      let errorCount = 0;
      let blockedCount = 0;

      // 处理每个服务器配置
      for (const [serverName, serverConfig] of Object.entries(mcpServers)) {
        // 检查是否为禁止的域名
        if (isBlockedDomain(serverConfig.url)) {
          blockedCount++;
          continue;
        }

        // 只处理类型为 sse 的服务器
        if (serverConfig.type === "sse" && serverConfig.url) {
          try {
            const response = await request.post<{
              code: number;
              msg: string;
              data: MCPServerInfo;
            }>(
              `${API_BASE_URL}/api/v3/mcp/servers`,
              {
                name: serverName,
                endpoint: serverConfig.url,
                project_id: projectId,
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
                timeout: REQUEST_TIMEOUT,
              }
            );

            if (response.data.code === 200) {
              addedCount++;
            } else {
              errorCount++;
              console.error(
                `添加服务器 ${serverName} 失败:`,
                response.data.msg
              );
            }
          } catch (error: any) {
            errorCount++;
            console.error(`添加服务器 ${serverName} 失败:`, error);
          }
        }
      }

      if (blockedCount > 0) {
        message.warning(`${blockedCount} 个服务器因域名限制被跳过`);
      }

      if (addedCount > 0) {
        message.success(`成功添加 ${addedCount} 个MCP服务器`);
        setModalVisible(false);
        fetchServers();
      }

      if (errorCount > 0) {
        message.warning(
          `${errorCount} 个MCP服务器添加失败，请查看控制台了解详情`
        );
      }
    } catch (error) {
      console.error("解析JSON配置失败:", error);
      setJsonParseError("JSON格式无效，请检查输入");
    }
  };

  // 删除MCP服务器
  const deleteServer = async (serverId: number) => {
    try {
      const response = await request.delete<{
        code: number;
        msg: string;
      }>(
        `${API_BASE_URL}/api/v3/mcp/servers/${serverId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          timeout: REQUEST_TIMEOUT,
        }
      );

      if (response.data.code === 200) {
        message.success("MCP服务器删除成功");
        fetchServers();
      } else {
        message.error(response.data.msg || "删除失败");
      }
    } catch (error: any) {
      console.error("删除MCP服务器失败:", error);
      message.error(error.response?.data?.msg || "删除失败");
    }
  };

  // 打开添加模态框
  const showAddModal = () => {
    setModalTitle("添加MCP服务器");
    setCurrentServer(null);
    setTestStatus(null);
    setServerTools([]);
    setJsonParseError(null);
    setActiveTab("form");

    form.resetFields();
    form.setFieldsValue({
      project_id: selectedProject || postChatProjects[0]?.id,
      enabled: false,
    });

    jsonForm.resetFields();
    jsonForm.setFieldsValue({
      project_id: selectedProject || postChatProjects[0]?.id,
      enabled: false,
    });

    setModalVisible(true);
  };

  // 打开编辑模态框
  const showEditModal = (server: MCPServerInfo) => {
    setModalTitle("编辑MCP服务器");
    setCurrentServer(server);
    setTestStatus(null);
    setServerTools([]);
    setActiveTab("form");

    form.setFieldsValue({
      name: server.name,
      endpoint: server.endpoint,
      project_id: server.project_id,
      enabled: server.enabled,
    });

    setModalVisible(true);
  };

  // 打开工具管理模态框
  const showToolsModal = async (server: MCPServerInfo) => {
    setSelectedServer(server);
    setScopedServerEnabled(server.enabled);
    setToolsModalVisible(true);
    await fetchServerTools(server.id);
  };

  // 项目选择变化时重新获取服务器列表并更新MCP启用状态
  const handleProjectChange = (projectId: number) => {
    setSelectedProject(projectId);
    fetchServers(projectId);

    // 更新当前项目的MCP启用状态
    const project = postChatProjects.find((p) => p.id === projectId);
    if (project) {
      setProjectMCPEnabled(project.enable_mcp);
    }
  };

  useEffect(() => {
    if (postChatProjects.length > 0) {
      if (!selectedProject) {
        const firstProjectId = postChatProjects[0].id;
        setSelectedProject(firstProjectId);
        fetchServers(firstProjectId);

        // 设置初始MCP启用状态
        const project = postChatProjects.find((p) => p.id === firstProjectId);
        if (project) {
          setProjectMCPEnabled(project.enable_mcp);
        }
      }
    }
  }, [postChatProjects, selectedProject, fetchServers]);

  // 启用/禁用项目的MCP功能
  const updateProjectMCPStatus = async (
    projectId: number,
    enabled: boolean
  ) => {
    try {
      const response = await request.patch<{
        code: number;
        msg: string;
      }>(
        `${API_BASE_URL}/api/v3/projects/${projectId}/mcp`,
        {
          enable_mcp: enabled,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          timeout: REQUEST_TIMEOUT,
        }
      );

      if (response.data.code === 200) {
        setProjectMCPEnabled(enabled); // 更新状态
        message.success(enabled ? "项目MCP功能已启用" : "项目MCP功能已禁用");
        await refreshProjects();
      } else {
        message.error(response.data.msg || "操作失败");
      }
    } catch (error: any) {
      console.error("更新项目MCP状态失败:", error);
      message.error(error.response?.data?.msg || "操作失败");
    }
  };

  // 自定义URL验证器
  const validateEndpoint = (rule: any, value: string) => {
    if (!value) {
      return Promise.reject("请输入服务器端点URL");
    }

    try {
      const url = new URL(value);

      // 检查是否为禁止的域名
      if (BLOCKED_DOMAINS.some((domain) => url.hostname.includes(domain))) {
        return Promise.reject("此域名已被限制使用，请使用其他MCP服务器");
      }

      return Promise.resolve();
    } catch (e) {
      return Promise.reject("请输入有效的URL");
    }
  };

  // 服务器列表表格列定义
  const columns = [
    {
      title: "服务器名称",
      dataIndex: "name",
      key: "name",
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: "服务器端点",
      dataIndex: "endpoint",
      key: "endpoint",
      ellipsis: { showTitle: false },
      render: (text: string) => (
        <Tooltip title={text}>
          <Text code>{text}</Text>
        </Tooltip>
      ),
    },
    {
      title: "状态",
      dataIndex: "enabled",
      key: "enabled",
      render: (enabled: boolean) => (
        <Tag
          color={enabled ? "success" : "default"}
          className="tool-status-tag"
        >
          {enabled ? "已启用" : "已禁用"}
        </Tag>
      ),
    },
    {
      title: "创建时间",
      dataIndex: "created_at",
      key: "created_at",
      render: (text: string) => new Date(text).toLocaleString("zh-CN"),
    },
    {
      title: "操作",
      key: "action",
      render: (_: any, record: MCPServerInfo) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<i className="ri-tools-line" />}
            onClick={() => showToolsModal(record)}
          >
            工具管理
          </Button>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => showEditModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除此MCP服务器吗？"
            description="删除后将无法恢复，且相关的工具配置将被清除。"
            onConfirm={() => deleteServer(record.id)}
            okText="确定"
            cancelText="取消"
            okButtonProps={{ danger: true }}
            icon={<ExclamationCircleOutlined style={{ color: "red" }} />}
          >
            <Button type="text" danger size="small" icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 工具列表表格列定义
  const toolColumns = [
    {
      title: "工具名称",
      dataIndex: "name",
      key: "name",
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: "描述",
      dataIndex: "description",
      key: "description",
      ellipsis: { showTitle: false },
      render: (text: string) => (
        <Tooltip title={text}>
          <span>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: "状态",
      key: "enabled",
      render: (_: unknown, record: ToolInfo) => (
        <Tag
          color={(selectedServer?.authorization_mode === "scoped" ? record.enabled : !record.disabled) ? "success" : "default"}
          className="tool-status-tag"
        >
          {(selectedServer?.authorization_mode === "scoped" ? record.enabled : !record.disabled) ? "已授权" : "未授权"}
        </Tag>
      ),
    },
    {
      title: "风险",
      width: 135,
      render: (_: unknown, record: ToolInfo) => selectedServer?.authorization_mode === "scoped" ? (
        <Select
          size="small"
          value={record.risk || "read"}
          onChange={(risk) => updateScopedTool(record.name, { risk })}
          options={[
            { value: "read", label: "只读" }, { value: "draft", label: "草稿" },
            { value: "write", label: "写入" }, { value: "sensitive", label: "敏感" },
            { value: "financial", label: "财务" }, { value: "irreversible", label: "不可逆" },
          ]}
        />
      ) : <Text type="secondary">旧版</Text>,
    },
    {
      title: "允许 Scope",
      width: 210,
      render: (_: unknown, record: ToolInfo) => selectedServer?.authorization_mode === "scoped" ? (
        <Select
          mode="tags"
          size="small"
          style={{ width: "100%" }}
          value={record.scopes || []}
          tokenSeparators={[","]}
          placeholder="例如 content:read"
          onChange={(scopes) => updateScopedTool(record.name, { scopes })}
        />
      ) : "-",
    },
    {
      title: "任务内上限",
      width: 105,
      render: (_: unknown, record: ToolInfo) => selectedServer?.authorization_mode === "scoped" ? (
        <InputNumber size="small" min={1} max={20} value={record.maxCallsPerExecution || 1}
          onChange={(value) => updateScopedTool(record.name, { maxCallsPerExecution: value || 1 })} />
      ) : "-",
    },
    {
      title: "操作",
      key: "action",
      render: (_: unknown, record: ToolInfo) => (
        <Switch
          checked={selectedServer?.authorization_mode === "scoped" ? Boolean(record.enabled) : !record.disabled}
          onChange={(checked) => toggleToolStatus(record.name, !checked)}
          checkedChildren="已启用"
          unCheckedChildren="已禁用"
        />
      ),
    },
  ];

  // JSON配置示例
  const jsonConfigExample = `{
  "mcpServers": {
    "bing-cn-mcp-server": {
      "type": "sse",
      "url": "https://mcp.api-inference.modelscope.cn/sse/XXXXXXXX"
    }
  }
}`;

  return (
    <>
      {postChatProjects.length === 0 ? (
        <Alert
          message="未找到时长计费项目"
          description="MCP客户端功能仅支持时长计费项目，请先创建一个时长计费项目。"
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      ) : (
        <>
          <div
            style={{
              marginBottom: 16,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <Text style={{ marginRight: 8 }}>选择项目：</Text>
              <Select
                value={selectedProject || undefined}
                onChange={handleProjectChange}
                style={{ width: 240 }}
                placeholder="选择要管理的项目"
                options={postChatProjects.map((project) => ({
                  value: project.id,
                  label: `${project.name} (时长计费)`,
                }))}
              />
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={showAddModal}
              disabled={servers.length >= 5}
            >
              添加MCP服务器
            </Button>
          </div>

          {/* 服务器列表卡片 */}
          <Card
            title={
              <span>
                <i
                  className="ri-computer-line"
                  style={{ marginRight: "8px", color: "#722ED1" }}
                ></i>
                MCP客户端服务器列表
              </span>
            }
            variant="borderless"
            className="shadow-card"
            style={{ marginBottom: 16, borderRadius: 8 }}
            extra={
              <Space>
                {selectedProject && (
                  <Switch
                    checkedChildren="MCP已启用"
                    unCheckedChildren="MCP已禁用"
                    checked={projectMCPEnabled}
                    onChange={(checked) =>
                      updateProjectMCPStatus(selectedProject, checked)
                    }
                  />
                )}
                <Text type="secondary">
                  {servers.length}/5 服务器
                  {servers.length >= 5 && " (已达上限)"}
                </Text>
              </Space>
            }
          >
            {loading ? (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <Spin
                  indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                />
                <p style={{ marginTop: 16 }}>加载MCP服务器列表中...</p>
              </div>
            ) : servers.length > 0 ? (
              <Table
                columns={columns}
                dataSource={servers}
                rowKey="id"
                pagination={false}
              />
            ) : (
              <Empty
                description={
                  <span>暂无MCP服务器，点击"添加MCP服务器"按钮创建</span>
                }
                style={{ padding: "40px 0" }}
              />
            )}

            <Alert
              message="什么是MCP客户端？"
              description={
                <div>
                  <p>
                    MCP客户端允许您连接到第三方MCP服务器，为您的AI助手提供额外的工具能力。
                  </p>
                  <p>
                    您可以为每个项目添加最多5个MCP
                    Server，并可以对每个服务器中的工具进行单独管理。
                  </p>
                  <p>
                    注意：添加外部MCP服务器可能存在安全风险，请确保只连接您信任的服务。
                  </p>
                </div>
              }
              type="info"
              showIcon
              style={{ marginTop: 16 }}
            />
          </Card>
        </>
      )}

      {/* 使用指南卡片 */}
      <Card
        title={
          <span>
            <i
              className="ri-book-open-line"
              style={{ marginRight: "8px", color: "#722ED1" }}
            ></i>
            MCP客户端使用指南
          </span>
        }
        variant="borderless"
        className="shadow-card"
        style={{ borderRadius: 8 }}
      >
        <Collapse
          bordered={false}
          defaultActiveKey={["1"]}
          items={[
            {
              key: "1",
              label: "如何添加第三方MCP服务器？",
              children: (
                <ol style={{ paddingLeft: 24 }}>
                  <li>点击"添加MCP服务器"按钮</li>
                  <li>
                    <strong>表单模式：</strong>输入服务器名称和端点URL
                  </li>
                  <li>
                    <strong>JSON模式：</strong>
                    粘贴符合格式的JSON配置，支持批量添加服务器
                  </li>
                  <li>选择关联的项目并设置启用状态</li>
                  <li>保存设置后，MCP服务器将被添加到您的项目中</li>
                </ol>
              ),
            },
            {
              key: "2",
              label: "MCP服务器推荐",
              children: (
                <div>
                  <p>以下是一些推荐的开源MCP服务器：</p>
                  <ul style={{ paddingLeft: 24 }}>
                    <li>
                      <a
                        href="https://www.modelscope.cn/mcp"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        ModelScopeMCP 广场
                      </a>{" "}
                      - 提供各种MCP服务的广场
                    </li>
                  </ul>
                  <Alert
                    message="安全提示"
                    description={
                      <div>
                        <p>
                          请注意，连接到MCP服务器后，HongMoAI将有权限对您的数据进行处理，因此，请不要连接能任何影响您隐私安全的MCP
                          Server。
                        </p>
                        <p>
                          确保您只连接到您信任的服务器，并了解其隐私政策和数据处理方式。
                        </p>
                      </div>
                    }
                    type="warning"
                    showIcon
                    style={{ marginTop: 8 }}
                  />
                </div>
              ),
            },
            {
              key: "3",
              label: "JSON配置说明",
              children: (
                <div>
                  <p>支持通过JSON配置批量添加MCP服务器，格式如下：</p>
                  <div
                    style={{
                      background: "#f6f8fa",
                      padding: "12px",
                      borderRadius: "6px",
                      fontFamily: "monospace",
                      whiteSpace: "pre",
                      overflowX: "auto",
                      marginBottom: "16px",
                    }}
                  >
                    {jsonConfigExample}
                  </div>
                  <p>
                    <strong>说明：</strong>
                  </p>
                  <ul style={{ paddingLeft: 24 }}>
                    <li>
                      <code>mcpServers</code>: 服务器配置对象
                    </li>
                    <li>
                      每个配置项的<code>key</code>将作为服务器名称
                    </li>
                    <li>
                      <code>type</code>: 目前支持"sse"类型的服务器
                    </li>
                    <li>
                      <code>url</code>: 服务器端点地址
                    </li>
                  </ul>
                </div>
              ),
            },
          ]}
        />
      </Card>

      {/* 添加/编辑MCP服务器模态框 */}
      <Modal
        title={modalTitle}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane
            tab={
              <span>
                <i className="ri-form-line" style={{ marginRight: 6 }} />
                表单模式
              </span>
            }
            key="form"
          >
            <Form form={form} layout="vertical" name="mcpServerForm">
              <Form.Item
                name="name"
                label="服务器名称"
                rules={[{ required: true, message: "请输入服务器名称" }]}
              >
                <Input placeholder="例如：lobehub-mcp-server" />
              </Form.Item>

              <Form.Item
                name="endpoint"
                label="服务器端点URL"
                rules={[
                  { required: true, message: "请输入服务器端点URL" },
                  { validator: validateEndpoint },
                ]}
              >
                <Input placeholder="例如：https://example.com/mcp" />
              </Form.Item>

              <Form.Item
                name="project_id"
                label="关联项目"
                rules={[{ required: true, message: "请选择关联的项目" }]}
              >
                <Select
                  placeholder="选择项目"
                  options={postChatProjects.map((project) => ({
                    value: project.id,
                    label: `${project.name} (时长计费)`,
                  }))}
                />
              </Form.Item>

              {currentServer?.authorization_mode === "legacy" ? (
                <Form.Item name="enabled" label="旧版服务器状态" valuePropName="checked">
                  <Switch checkedChildren="启用" unCheckedChildren="禁用" />
                </Form.Item>
              ) : (
                <Alert showIcon type="info" message="新连接保存后默认关闭"
                  description="先测试并发现工具，再到“工具管理”逐项配置风险、Scope 和调用上限；普通资料编辑不会授予执行权限。"
                  style={{ marginBottom: 16 }} />
              )}

              {testStatus && (
                <Alert
                  message={testStatus.success ? "连接测试成功" : "连接测试失败"}
                  description={testStatus.message}
                  type={testStatus.success ? "success" : "error"}
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}

              {testStatus?.success && serverTools.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <Divider>检测到的工具 ({serverTools.length})</Divider>
                  <ul style={{ maxHeight: 200, overflow: "auto" }}>
                    {serverTools.map((tool, index) => (
                      <li key={index}>
                        <Text strong>{tool.name}</Text>
                        {tool.description && (
                          <Text type="secondary"> - {tool.description}</Text>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: 24,
                }}
              >
                <Button key="cancel" onClick={() => setModalVisible(false)}>
                  取消
                </Button>
                <Space>
                  <Button
                    key="test"
                    type="default"
                    onClick={() => testMCPServer()}
                    loading={testLoading}
                  >
                    测试连接
                  </Button>
                  <Button
                    key="submit"
                    type="primary"
                    onClick={() => {
                      form.validateFields().then((values) => {
                        handleAddOrUpdateServer(values);
                      });
                    }}
                  >
                    保存
                  </Button>
                </Space>
              </div>
            </Form>
          </TabPane>
          <TabPane
            tab={
              <span>
                <CodeOutlined style={{ marginRight: 6 }} />
                JSON配置
              </span>
            }
            key="json"
            disabled={!!currentServer}
          >
            <Form form={jsonForm} layout="vertical" name="mcpJsonForm">
              <Form.Item
                name="jsonConfig"
                label="MCP服务器JSON配置"
                rules={[{ required: true, message: "请输入JSON配置" }]}
                help="粘贴符合格式的JSON配置，支持批量添加MCP服务器"
              >
                <TextArea
                  placeholder={jsonConfigExample}
                  autoSize={{ minRows: 8, maxRows: 12 }}
                />
              </Form.Item>

              <Form.Item
                name="project_id"
                label="关联项目"
                rules={[{ required: true, message: "请选择关联的项目" }]}
              >
                <Select
                  placeholder="选择项目"
                  options={postChatProjects.map((project) => ({
                    value: project.id,
                    label: `${project.name} (时长计费)`,
                  }))}
                />
              </Form.Item>

              <Alert showIcon type="info" message="批量添加的连接均默认关闭"
                description="每个服务器都需要连接测试和逐工具后端授权，JSON 配置不能直接开启远端能力。"
                style={{ marginBottom: 16 }} />

              {jsonParseError && (
                <Alert
                  message="JSON解析错误"
                  description={jsonParseError}
                  type="error"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: 24,
                }}
              >
                <Button key="cancel" onClick={() => setModalVisible(false)}>
                  取消
                </Button>
                <Button
                  key="submit"
                  type="primary"
                  onClick={() => {
                    jsonForm.validateFields().then(handleAddServersByJson);
                  }}
                >
                  批量添加
                </Button>
              </div>
            </Form>
          </TabPane>
        </Tabs>
      </Modal>

      {/* 工具管理模态框 */}
      <Modal
        title={`管理工具 - ${selectedServer?.name || ""}`}
        open={toolsModalVisible}
        onCancel={() => setToolsModalVisible(false)}
        footer={[
          ...(selectedServer?.authorization_mode === "scoped" ? [
            <Button key="save-auth" type="primary" loading={authorizationSaving} onClick={() => void saveScopedAuthorization()}>
              保存后端授权
            </Button>,
          ] : []),
          <Button
            key="close"
            type="primary"
            onClick={() => setToolsModalVisible(false)}
          >
            关闭
          </Button>,
        ]}
        width={selectedServer?.authorization_mode === "scoped" ? 1080 : 700}
      >
        {selectedServer?.authorization_mode === "scoped" && (
          <Space direction="vertical" size={12} style={{ width: "100%", marginBottom: 16 }}>
            <Alert
              showIcon
              type="warning"
              message="发现工具不代表允许执行"
              description="只有这里保存的逐工具策略才是后端授权。写入、敏感、财务和不可逆能力在 Runtime 执行时仍需经过对应确认；模型或浏览器参数不能扩大 Scope。"
            />
            <Space>
              <Text strong>允许该 MCP 参与 Runtime 执行</Text>
              <Switch checked={scopedServerEnabled} onChange={setScopedServerEnabled} />
            </Space>
          </Space>
        )}
        {toolsLoading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            />
            <p style={{ marginTop: 16 }}>加载工具列表中...</p>
          </div>
        ) : serverTools.length > 0 ? (
          <Table
            columns={toolColumns}
            dataSource={serverTools}
            rowKey="name"
            pagination={false}
            size="middle"
          />
        ) : (
          <Empty description="未检测到可用工具" />
        )}
      </Modal>
    </>
  );
};

export default MCPClientTab;
